import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

const parseDay = (s) => {
  if (!s) return null;
  const iso = String(s).slice(0, 10);
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

function DueBadge({ due }) {
  const d = parseDay(due);
  if (!d) return <span className="label px-2 py-0.5" style={{ background: "#e5e7eb", color: "#374151", border: "1px solid #e5e7eb" }}>No date</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  const base = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  let bg = "#dbeafe", color = "#1e3a8a", label = `${base} · in ${days}d`;
  if (days < 0) { bg = "#fee2e2"; color = "#991b1b"; label = `${base} · Overdue ${-days}d`; }
  else if (days === 0) { bg = "#fef3c7"; color = "#78350f"; label = `${base} · Today`; }
  else if (days <= 3) { bg = "#fef3c7"; color = "#78350f"; label = `${base} · in ${days}d`; }
  return (
    <span className="label px-2 py-0.5" style={{ background: bg, color, border: `1px solid ${bg}`, fontWeight: 700 }}>
      {label}
    </span>
  );
}

const KIND_COLORS = {
  "Onboarding": { bg: "#e0f2fe", color: "#075985" },
  "Offboarding": { bg: "#e0e7ff", color: "#3730a3" },
  "Sample": { bg: "#fce7f3", color: "#9d174d" },
  "Payment": { bg: "#fef3c7", color: "#78350f" },
  "Marketing": { bg: "#dcfce7", color: "#166534" },
  "Admin task": { bg: "#f3f4f6", color: "#374151" },
  "Verification": { bg: "#ede9fe", color: "#5b21b6" },
};

function KindPill({ kind }) {
  const c = KIND_COLORS[kind] || KIND_COLORS["Admin task"];
  return (
    <span className="label px-2 py-0.5" style={{ background: c.bg, color: c.color, border: `1px solid ${c.bg}`, fontWeight: 700 }}>
      {kind}
    </span>
  );
}

/**
 * Render a list of required-action items.
 * items: [{ key, kind, title, due_date, link, actor: "client" | "agency" }]
 * groupByActor: when true, splits into two sections.
 */
export default function RequiredActionsPanel({ items, groupByActor = false, emptyLabel = "Nothing pending 🎉", testid = "required-actions" }) {
  const sorted = [...items].sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"));
  if (sorted.length === 0) {
    return (
      <div className="panel p-8 text-center" data-testid={`${testid}-empty`}>
        <CheckCircle size={28} weight="bold" className="mx-auto mb-2" />
        <p className="label">{emptyLabel}</p>
      </div>
    );
  }
  if (!groupByActor) {
    return (
      <div className="panel p-2 md:p-4" data-testid={testid}>
        <div className="space-y-1">
          {sorted.map(it => <Row key={it.key} it={it} />)}
        </div>
      </div>
    );
  }
  const client = sorted.filter(i => i.actor === "client");
  const agency = sorted.filter(i => i.actor === "agency");
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="panel p-2 md:p-4" data-testid={`${testid}-client`}>
        <div className="px-3 pt-2 pb-1 flex items-baseline justify-between">
          <p className="label" style={{ fontWeight: 700 }}>Due by client · {client.length}</p>
        </div>
        {client.length === 0 ? (
          <p className="label text-center py-6" style={{ color: "var(--text-dim)" }}>Nothing waiting on the client</p>
        ) : (
          <div className="space-y-1">
            {client.map(it => <Row key={it.key} it={it} />)}
          </div>
        )}
      </div>
      <div className="panel p-2 md:p-4" data-testid={`${testid}-agency`}>
        <div className="px-3 pt-2 pb-1 flex items-baseline justify-between">
          <p className="label" style={{ fontWeight: 700 }}>Due by agency · {agency.length}</p>
        </div>
        {agency.length === 0 ? (
          <p className="label text-center py-6" style={{ color: "var(--text-dim)" }}>Inbox zero on the agency side</p>
        ) : (
          <div className="space-y-1">
            {agency.map(it => <Row key={it.key} it={it} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ it }) {
  const inner = (
    <div className="hair p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 hover:bg-black/5 transition-colors rounded"
      data-testid={`required-item-${it.key}`}>
      <div className="min-w-0 flex-1 flex flex-col md:flex-row md:items-center gap-2">
        <KindPill kind={it.kind} />
        <p className="text-sm font-medium" style={{ overflowWrap: "anywhere" }}>{it.title}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DueBadge due={it.due_date} />
        {it.link && <ArrowRight size={14} weight="bold" style={{ color: "var(--text-dim)" }} />}
      </div>
    </div>
  );
  if (!it.link) return inner;
  return <Link to={it.link} style={{ color: "inherit" }} className="no-underline block">{inner}</Link>;
}
